import React from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  CompassColor,
  Image,
  Link,
  Spacing,
  Stack,
  Text,
  useMediaQuery,
} from "@noom/wax-component-library";

import type {
  QuestionComponentProps,
  QuestionDefinition,
} from "@/components/survey/Question";
import { useGeolocation, useTrackEvent } from "@/hooks";
import { shouldShowPhqSupportResourcesInterstitial } from "@/utils/survey/phq";

import { BaseInterstitial } from "../../core";

const QUESTION_ID = "phqSupportResources";

const PhqSupportResources: React.FC<QuestionComponentProps> = ({
  onClickBack,
  onClickNext,
}) => {
  const { country, isLoading } = useGeolocation();
  const [isBrowser] = useMediaQuery("(min-width: 1160px)");
  const { t } = useTranslation();
  const { trackAnonEvent } = useTrackEvent();

  // Default to non-US if location is unavailable or still loading
  const isUS = !isLoading && country === "US";

  const handleClickLink = (buttonName: string) => {
    trackAnonEvent("b2bEnrollmentAnonButtonClicked", {
      buttonName,
      pageId: QUESTION_ID,
    });
  };

  return (
    <BaseInterstitial
      backgroundColor={CompassColor.blueberry}
      ctaText={t("phqSupportResources.ctaText")}
      onClickBack={onClickBack}
      onClickNext={onClickNext}
      questionId={QUESTION_ID}
      width={isBrowser ? "600px" : undefined}
    >
      <Stack marginBottom="var(--spacing-m)" spacing={Spacing[8]}>
        <Image
          height={{ base: "48px", lg: "64px" }}
          margin="var(--spacing-l) 0px var(--spacing-s) 0px"
          src="/assets/img/diamond-tarocco.svg"
        />
        <Text
          color={CompassColor.white}
          fontFamily="Untitled Serif"
          fontSize="28px"
          fontWeight="500"
          lineHeight="36px"
        >
          {t("phqSupportResources.heading")}
        </Text>
        <Text color={CompassColor.white} fontSize="18px" lineHeight="26px">
          {t("phqSupportResources.message1")}
        </Text>
        <Text color={CompassColor.white} fontSize="18px" lineHeight="26px">
          <Trans
            components={{
              helplineLink: (
                <Link
                  color={CompassColor.white}
                  href="https://findahelpline.com/"
                  isExternal
                  onClick={() => handleClickLink("helplineLink")}
                  textDecoration="underline"
                />
              ),
              smsLink: (
                <Link
                  color={CompassColor.white}
                  fontWeight="700"
                  href="sms:741741?body=HOME"
                  isExternal
                  onClick={() => handleClickLink("smsLink")}
                />
              ),
            }}
            context={isUS ? "us" : "default"}
            i18nKey="phqSupportResources.message2"
          />
        </Text>
      </Stack>
    </BaseInterstitial>
  );
};

export const Q_PHQ_SUPPORT_RESOURCES: QuestionDefinition = {
  id: QUESTION_ID,
  supportedPrograms: [
    "WEIGHT",
    "MOOD",
    "DPP",
    "DIABETES_LIFESTYLE",
    "MED",
    "NMPBA",
  ],
  shouldShowQuestion: ({ surveyAnswers }) =>
    shouldShowPhqSupportResourcesInterstitial(surveyAnswers),
  component: PhqSupportResources,
};

export const Q_RETRIAGE_PHQ_SUPPORT_RESOURCES: QuestionDefinition = {
  id: QUESTION_ID,
  supportedPrograms: ["MED", "NMPBA"],
  shouldShowQuestion: ({ surveyAnswers }) =>
    shouldShowPhqSupportResourcesInterstitial(surveyAnswers),
  component: PhqSupportResources,
};
